/**
 * Feature Flag Criteria
 *
 * Criteria are rules that evaluate whether a user matches certain conditions.
 * Each criterion receives a user context object and a data value from the feature config.
 */

import type { Criterion } from 'fflip';
import type { FeatureFlagUser } from './types';

/**
 * Check if user is a paid Paramount+ subscriber.
 * Usage: { isPaidUser: true }
 */
const isPaidUser: Criterion<FeatureFlagUser, boolean> = {
  id: 'isPaidUser',
  check: (user, isPaid) => {
    const isSubscriber = user.paramountPlus?.subscriber === true;
    return isPaid ? isSubscriber : !isSubscriber;
  },
};

/**
 * Check if user has a specific Paramount+ package.
 * Usage: { hasPackage: 'CBS_ALL_ACCESS_AD_FREE_PACKAGE' }
 */
const hasPackage: Criterion<FeatureFlagUser, string | string[]> = {
  id: 'hasPackage',
  check: (user, packages) => {
    const userPackage = user.paramountPlus?.userPackage;
    if (!userPackage) return false;

    const allowedPackages = Array.isArray(packages) ? packages : [packages];
    return allowedPackages.includes(userPackage);
  },
};

/**
 * Check if user is authenticated with Adobe (MVPD/cable provider).
 * Usage: { isAdobeAuth: true }
 */
const isAdobeAuth: Criterion<FeatureFlagUser, boolean> = {
  id: 'isAdobeAuth',
  check: (user, isAuth) => {
    const hasAdobeAuth = !!user.adobe?.userId;
    return isAuth ? hasAdobeAuth : !hasAdobeAuth;
  },
};

/**
 * Check if user's MVPD matches allowed list.
 * Usage: { hasMvpd: ['comcast', 'spectrum'] }
 */
const hasMvpd: Criterion<FeatureFlagUser, string | string[]> = {
  id: 'hasMvpd',
  check: (user, mvpds) => {
    const userMvpd = user.adobe?.mvpd;
    if (!userMvpd) return false;

    const allowedMvpds = Array.isArray(mvpds) ? mvpds : [mvpds];
    return allowedMvpds.some(
      mvpd => userMvpd.toLowerCase() === mvpd.toLowerCase(),
    );
  },
};

/**
 * Percentage-based rollout using user ID.
 * Usage: { percentageOfUsers: 0.5 } (50% of users)
 */
const percentageOfUsers: Criterion<FeatureFlagUser, number> = {
  id: 'percentageOfUsers',
  check: (user, percent) => {
    // Use a hash of user ID for consistent assignment
    const userId = user.paramountPlus?.userId || user.adobe?.userId || user.deviceId;
    if (!userId) return false;

    // Simple hash: sum of char codes mod 100
    const hash = String(userId)
      .split('')
      .reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return (hash % 100) < (percent * 100);
  },
};

/**
 * Allow specific user IDs (for testing/beta access).
 * Usage: { allowUserIds: [123, 456] }
 */
const allowUserIds: Criterion<FeatureFlagUser, (string | number)[]> = {
  id: 'allowUserIds',
  check: (user, allowedIds) => {
    const userId = user.paramountPlus?.userId || user.adobe?.userId;
    if (!userId) return false;

    return allowedIds.some(id => String(id) === String(userId));
  },
};

/**
 * Allow specific device IDs (for testing on specific devices).
 * Usage: { allowDeviceIds: ['device-abc', 'device-xyz'] }
 */
const allowDeviceIds: Criterion<FeatureFlagUser, string[]> = {
  id: 'allowDeviceIds',
  check: (user, allowedIds) => {
    if (!user.deviceId) return false;
    return allowedIds.includes(user.deviceId);
  },
};

/**
 * Check platform type.
 * Usage: { platform: 'samsung' } or { platform: ['samsung', 'lg'] }
 */
const platform: Criterion<FeatureFlagUser, string | string[]> = {
  id: 'platform',
  check: (user, platforms) => {
    if (!user.platform) return false;

    const allowedPlatforms = Array.isArray(platforms) ? platforms : [platforms];
    return allowedPlatforms.some(
      p => user.platform?.toLowerCase() === p.toLowerCase(),
    );
  },
};

/**
 * Check if user is in specific DMA (Designated Market Area).
 * Usage: { inDma: ['501', '803'] }
 */
const inDma: Criterion<FeatureFlagUser, string | string[]> = {
  id: 'inDma',
  check: (user, dmas) => {
    if (!user.dma) return false;

    const allowedDmas = Array.isArray(dmas) ? dmas : [dmas];
    return allowedDmas.includes(user.dma);
  },
};

/**
 * Check environment (development, staging, production).
 * Usage: { environment: 'development' }
 */
const environment: Criterion<FeatureFlagUser, string | string[]> = {
  id: 'environment',
  check: (user, envs) => {
    if (!user.environment) return false;

    const allowedEnvs = Array.isArray(envs) ? envs : [envs];
    return allowedEnvs.includes(user.environment);
  },
};

/**
 * All available criteria for feature flag evaluation.
 * Cast to allow mixed criterion data types in the array.
 */
export const criteria: Criterion<FeatureFlagUser, unknown>[] = [
  isPaidUser as Criterion<FeatureFlagUser, unknown>,
  hasPackage as Criterion<FeatureFlagUser, unknown>,
  isAdobeAuth as Criterion<FeatureFlagUser, unknown>,
  hasMvpd as Criterion<FeatureFlagUser, unknown>,
  percentageOfUsers as Criterion<FeatureFlagUser, unknown>,
  allowUserIds as Criterion<FeatureFlagUser, unknown>,
  allowDeviceIds as Criterion<FeatureFlagUser, unknown>,
  platform as Criterion<FeatureFlagUser, unknown>,
  inDma as Criterion<FeatureFlagUser, unknown>,
  environment as Criterion<FeatureFlagUser, unknown>,
];

export default criteria;
